import {
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Skeleton,
  Switch,
  Tag,
  Upload,
  message,
} from "antd";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { uploadFile } from "../../services/upload.service";
import FilePreview from "./FilePreview";
import GroupBadge, { isGroupRow } from "./GroupBadge";

const { TextArea } = Input;

/**
 * "Curriculum" tab — the class-level analogue of a senior Module.
 *
 * Admins see drafts alongside published entries (the server returns drafts only
 * to admins); every other role sees published entries only, so there is no draft
 * badge to hide client-side.
 */
function BoardPosts({ api, canEdit, viewOnly = false }) {
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [viewPost, setViewPost] = useState(null);
  const [files, setFiles] = useState([]);
  const [urls, setUrls] = useState([]);
  const [uploading, setUploading] = useState(false);

  const posts = useQuery(api.keys.posts, api.fetchPosts, {
    enabled: api.enabled,
  });

  // A group post is read-only on a branch board. In group scope every post is
  // the group's own, so it stays editable there.
  const rowEditable = (record) =>
    canEdit && (api.scope === "group" || !isGroupRow(record));

  const invalidate = () => {
    queryClient.invalidateQueries(api.keys.posts);
    queryClient.invalidateQueries(api.keys.board);
  };

  const onError = (err) => {
    message.error(
      err?.response?.data?.error?.errors?.[0] || "Something went wrong",
    );
  };

  const closeForm = () => {
    setShowForm(false);
    setEditId(null);
    setFiles([]);
    setUrls([]);
    form.resetFields();
  };

  const saveMutation = useMutation(
    (payload) => (editId ? api.updatePost(editId, payload) : api.createPost(payload)),
    {
      onSuccess: () => {
        message.success(editId ? "Curriculum updated" : "Curriculum created");
        closeForm();
        invalidate();
      },
      onError,
    },
  );

  const deleteMutation = useMutation(api.deletePost, {
    onSuccess: () => {
      message.success("Curriculum deleted");
      setViewPost(null);
      invalidate();
    },
    onError,
  });

  useEffect(() => {
    if (!editId) return;
    const post = posts.data?.find((p) => p._id === editId);
    if (!post) return;
    form.setFieldsValue({
      title: post.title,
      content: post.content,
      published: post.published,
    });
    setFiles(post.files ?? []);
    setUrls(post.urls ?? []);
  }, [editId, posts.data, form]);

  async function handleUpload({ file }) {
    try {
      setUploading(true);
      const res = await uploadFile({ file }, "docs");
      if (res) setFiles((prev) => [...prev, res]);
      message.success("File uploaded");
    } catch (err) {
      message.error("Upload failed");
    } finally {
      setUploading(false);
    }
  }

  const onFinish = (values) => {
    saveMutation.mutate({
      title: values.title,
      content: values.content ?? "",
      published: !!values.published,
      files,
      // Drop half-filled rows rather than persisting a link with no target.
      urls: urls.filter((u) => (u?.url ?? "").trim()),
    });
  };

  if (posts.isLoading) {
    return (
      <div className="pt-8">
        <div className="bg-white p-8 rounded-xl">
          <Skeleton active />
        </div>
      </div>
    );
  }

  return (
    <div className="pt-8">
      <div className="bg-white p-6 rounded-xl">
        <div className="flex justify-between items-center pb-4">
          <div style={{ color: "#E46E31" }} className="text-2xl font-semibold">
            Curriculum
          </div>
          {canEdit && (
            <Button
              size="large"
              type="primary"
              onClick={() => {
                setEditId(null);
                setFiles([]);
                form.resetFields();
                setShowForm(true);
              }}
            >
              Add Curriculum
            </Button>
          )}
        </div>

        {posts.data?.length ? (
          <div className="flex flex-wrap gap-4">
            {posts.data.map((post) => {
              const fileCount = post.files?.length || 0;
              const linkCount = post.urls?.length || 0;
              const extras = [
                fileCount && `${fileCount} file${fileCount > 1 ? "s" : ""}`,
                linkCount && `${linkCount} link${linkCount > 1 ? "s" : ""}`,
              ].filter(Boolean);
              const showStatus =
                canEdit || (api.scope === "class" && isGroupRow(post));

              return (
                <div
                  key={post._id}
                  onClick={() => setViewPost(post)}
                  className="cursor-pointer bg-white flex flex-col"
                  style={{
                    width: 200,
                    minHeight: 150,
                    padding: 14,
                    gap: 8,
                    border: "1px solid #E5E7EB",
                    borderRadius: 12,
                    transition: "box-shadow .15s ease, border-color .15s ease, transform .15s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#00627B";
                    e.currentTarget.style.boxShadow = "0 6px 16px rgba(0,0,0,0.08)";
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#E5E7EB";
                    e.currentTarget.style.boxShadow = "none";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  {/* Status first, above the title, so it never covers it at this
                      width. Only rendered when there is something to show. Ant
                      Design tags rather than Tailwind colour classes: the project's
                      Tailwind palette does not include the default greens. */}
                  {showStatus && (
                    <div className="flex items-center flex-wrap" style={{ gap: 4 }}>
                      {canEdit && (
                        <Tag
                          color={post.published ? "success" : "warning"}
                          style={{ marginInlineEnd: 0, fontSize: 11 }}
                        >
                          {post.published ? "Published" : "Draft"}
                        </Tag>
                      )}
                      {api.scope === "class" && isGroupRow(post) && (
                        <GroupBadge compact />
                      )}
                    </div>
                  )}

                  <div
                    style={{
                      color: "#00627B",
                      fontSize: 15,
                      fontWeight: 600,
                      lineHeight: 1.3,
                      wordBreak: "break-word",
                    }}
                  >
                    {post.title}
                  </div>

                  {!!post.content && (
                    <div
                      style={{
                        color: "#6B7280",
                        fontSize: 13,
                        lineHeight: 1.45,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {post.content}
                    </div>
                  )}

                  <div
                    className="flex items-center justify-between"
                    style={{
                      marginTop: "auto",
                      paddingTop: 8,
                      borderTop: "1px solid #F0F0F0",
                      fontSize: 11,
                      color: "#9CA3AF",
                    }}
                  >
                    <span>{extras.join(" · ")}</span>
                    <span>{moment(post.createdAt).format("DD MMM YYYY")}</span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center text-gray-400 py-10">
            {api.scope === "group"
              ? "No group curriculum yet. Anything you add here appears on every Junior branch."
              : "No curriculum has been added for this class yet."}
          </div>
        )}
      </div>

      {/* ------------------------------ Read view ----------------------------- */}
      <Modal
        width={700}
        open={!!viewPost}
        onCancel={() => setViewPost(null)}
        footer={null}
        destroyOnClose
      >
        <h3 className="text-xl font-bold pb-1">{viewPost?.title}</h3>
        <div className="text-xs text-gray-400 pb-4">
          {viewPost?.createdAt &&
            moment(viewPost.createdAt).format("MMMM Do YYYY")}
        </div>
        <div style={{ whiteSpace: "pre-wrap" }} className="text-base pb-4">
          {viewPost?.content}
        </div>

        {!!viewPost?.files?.length && (
          <div className="pb-4">
            <div className="font-semibold pb-2">Attachments</div>
            <ul className="space-y-2">
              {viewPost.files.map((file) => (
                <li key={file?.id}>
                  <FilePreview file={file} viewOnly={viewOnly} />
                </li>
              ))}
            </ul>
          </div>
        )}

        {!!viewPost?.urls?.length && (
          <div className="pb-4">
            <div className="font-semibold pb-2">Links</div>
            <ul>
              {viewPost.urls.map((link, index) => (
                <li key={index}>
                  <a
                    href={link?.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-500"
                  >
                    {link?.title || link?.url}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        {rowEditable(viewPost) && (
          <div className="flex justify-end gap-2 pt-2">
            <Popconfirm
              title="Delete this curriculum?"
              onConfirm={() => deleteMutation.mutate(viewPost._id)}
              okText="Yes"
              cancelText="No"
            >
              <Button danger>Delete</Button>
            </Popconfirm>
            <Button
              type="primary"
              onClick={() => {
                setEditId(viewPost._id);
                setViewPost(null);
                setShowForm(true);
              }}
            >
              Edit
            </Button>
          </div>
        )}
      </Modal>

      {/* ------------------------------ Edit form ----------------------------- */}
      <Modal
        width={700}
        open={showForm}
        onCancel={closeForm}
        footer={null}
        destroyOnClose
      >
        <h3 className="text-lg font-bold pb-3">
          {editId ? "Edit Curriculum" : "Create Curriculum"}
        </h3>
        <Form form={form} layout="vertical" onFinish={onFinish} autoComplete="off">
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: "Please input a title" }]}
          >
            <Input maxLength={200} placeholder="Enter title" />
          </Form.Item>

          <Form.Item name="content" label="Description">
            <TextArea
              rows={5}
              className="resize-none"
              maxLength={10000}
              placeholder="What should families know about this?"
            />
          </Form.Item>

          <Form.Item label="Attachments">
            <Upload
              multiple
              showUploadList={false}
              customRequest={handleUpload}
            >
              <Button loading={uploading}>Click to Upload</Button>
            </Upload>
            {!!files.length && (
              <ul className="pt-4 space-y-3">
                {files.map((file) => (
                  <li
                    key={file?.id}
                    className="flex justify-between items-center gap-4"
                  >
                    <a
                      href={file?.files?.original}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {file?.files?.name || "Attachment"}
                    </a>
                    <Button
                      danger
                      type="text"
                      onClick={() =>
                        setFiles((prev) => prev.filter((f) => f.id !== file.id))
                      }
                    >
                      Remove
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </Form.Item>

          <Form.Item label="Links" className="pt-2">
            {urls.map((link, index) => (
              <div key={index} className="flex items-center gap-4 pb-4">
                <Input
                  placeholder="Label (optional)"
                  style={{ width: "35%" }}
                  value={link.title ?? ""}
                  onChange={(e) =>
                    setUrls((prev) =>
                      prev.map((u, i) =>
                        i === index ? { ...u, title: e.target.value } : u,
                      ),
                    )
                  }
                />
                <Input
                  placeholder="https://..."
                  value={link.url ?? ""}
                  onChange={(e) =>
                    setUrls((prev) =>
                      prev.map((u, i) =>
                        i === index ? { ...u, url: e.target.value } : u,
                      ),
                    )
                  }
                />
                <Button
                  danger
                  type="text"
                  onClick={() =>
                    setUrls((prev) => prev.filter((_, i) => i !== index))
                  }
                >
                  Remove
                </Button>
              </div>
            ))}
            <Button
              onClick={() => setUrls((prev) => [...prev, { title: "", url: "" }])}
            >
              Add Link
            </Button>
          </Form.Item>

          <Form.Item
            name="published"
            label="Publish"
            valuePropName="checked"
            extra="Drafts stay hidden from instructors, parents and students."
          >
            <Switch />
          </Form.Item>

          <div className="flex justify-end items-center pt-4">
            <div className="inline-flex items-center space-x-2">
              <Button size="large" onClick={closeForm}>
                Cancel
              </Button>
              <Button
                size="large"
                type="primary"
                htmlType="submit"
                loading={saveMutation.isLoading}
              >
                {editId ? "Update" : "Save"}
              </Button>
            </div>
          </div>
        </Form>
      </Modal>
    </div>
  );
}

export default BoardPosts;
